import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Plus, Trash2, Calendar as CalendarIcon, Clock } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/modules/shared/ui/components/popover"
import { Calendar } from "@/modules/shared/ui/components/calendar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modules/shared/ui/components/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modules/shared/ui/components/tooltip"
import { format } from "date-fns"
import { cn } from "@/lib/utils"

interface CreateExpertInterviewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onConfirm: (data: { title: string, notes: string, timeSlots: { startTime: string, endTime: string }[] }) => void
    isSubmitting: boolean
}

// Generate time slots in 15-minute intervals
const generateTimeOptions = () => {
    const times = []
    for (let i = 0; i < 24; i++) {
        for (let j = 0; j < 60; j += 15) {
            const hour = i.toString().padStart(2, '0')
            const minute = j.toString().padStart(2, '0')
            times.push(`${hour}:${minute}`)
        }
    }
    return times
}

const timeOptions = generateTimeOptions()

const getTimeOptionsForSlot = (slot: { date: Date | undefined, startTime: string, endTime: string }) => {
    if (!slot.date) return timeOptions

    const now = new Date()
    const slotDate = new Date(slot.date)
    const isToday = slotDate.toDateString() === now.toDateString()

    if (!isToday) return timeOptions

    // For today, filter out past times
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    return timeOptions.filter(time => time > currentTime)
}

const getEndTimeOptionsForSlot = (slot: { date: Date | undefined, startTime: string, endTime: string }) => {
    const availableStartTimes = getTimeOptionsForSlot(slot)
    if (!slot.startTime) return availableStartTimes
    return availableStartTimes.filter(time => time > slot.startTime)
}

const isSlotInPast = (slot: { date: Date | undefined, startTime: string, endTime: string }) => {
    if (!slot.date || !slot.startTime) return false
    const now = new Date()
    const slotDate = new Date(slot.date)
    const isToday = slotDate.toDateString() === now.toDateString()
    if (!isToday) return false
    const [hours, minutes] = slot.startTime.split(':').map(Number)
    const slotStart = new Date(slotDate)
    slotStart.setHours(hours, minutes, 0, 0)
    return slotStart <= now
}

export function CreateExpertInterviewDialog({
    open,
    onOpenChange,
    onConfirm,
    isSubmitting
}: CreateExpertInterviewDialogProps) {
    const [title, setTitle] = useState("")
    const [notes, setNotes] = useState("")
    const [calendarOpen, setCalendarOpen] = useState<{ [key: number]: boolean }>({})

    const [timeSlots, setTimeSlots] = useState<{ date: Date | undefined, startTime: string, endTime: string }[]>([
        { date: undefined, startTime: "", endTime: "" }
    ])

    const handleAddSlot = () => {
        if (timeSlots.length < 3) {
            setTimeSlots([...timeSlots, { date: undefined, startTime: "", endTime: "" }])
        }
    }

    const handleRemoveSlot = (index: number) => {
        const newSlots = [...timeSlots]
        newSlots.splice(index, 1)
        setTimeSlots(newSlots)
    }

    const updateSlot = (index: number, field: 'date' | 'startTime' | 'endTime', value: string | Date) => {
        const newSlots = [...timeSlots]
        newSlots[index] = { ...newSlots[index], [field]: value }

        if (field === 'startTime' && value) {
            const [hours, minutes] = (value as string).split(':').map(Number)
            const endHour = (hours + 1) % 24
            const endTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
            newSlots[index] = { ...newSlots[index], endTime }
        }

        setTimeSlots(newSlots)
    }

    const handleSubmit = () => {
        if (!title.trim()) return;

        const formattedSlots = timeSlots
            .filter(slot => slot.date && slot.startTime && slot.endTime)
            .map(slot => {
                const dateStr = format(slot.date!, 'yyyy-MM-dd')
                return {
                    startTime: new Date(`${dateStr}T${slot.startTime}`).toISOString(),
                    endTime: new Date(`${dateStr}T${slot.endTime}`).toISOString()
                }
            });

        if (formattedSlots.length === 0) return;

        onConfirm({
            title,
            notes,
            timeSlots: formattedSlots
        })
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] sm:max-w-[650px] max-h-[90vh] overflow-y-auto bg-card p-0 gap-0 border-border shadow-2xl rounded-2xl">
                <DialogHeader className="bg-muted/50 px-4 sm:px-6 py-4 sm:py-5 border-b border-border">
                    <DialogTitle className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
                        <div className="w-1 h-4 sm:h-5 bg-primary rounded-full" />
                        Schedule Expert Interview
                    </DialogTitle>
                    <DialogDescription className="text-sm sm:text-base text-muted-foreground mt-1.5">
                        Set up an expert interview by proposing time slots for the candidate.
                    </DialogDescription>
                </DialogHeader>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    <div className="space-y-2 sm:space-y-3">
                        <Label htmlFor="title" className="text-sm font-semibold text-foreground">Interview Title</Label>
                        <Input
                            id="title"
                            placeholder="e.g. Technical Deep Dive"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="h-10 sm:h-11 border-border focus-visible:ring-ring transition-all text-foreground placeholder:text-muted-foreground"
                        />
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                            <Label className="text-sm font-semibold text-foreground">Proposed Time Slots (Max 3)</Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={handleAddSlot}
                                                disabled={timeSlots.length >= 3}
                                                className="w-full sm:w-auto bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 shadow-sm transition-all h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Plus className="size-3.5 mr-1.5" /> Add Slot
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    {timeSlots.length >= 3 && (
                                        <TooltipContent>
                                            <p>Maximum 3 time slots allowed</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <div className="space-y-3">
                            {timeSlots.map((slot, index) => {
                                const slotInPast = isSlotInPast(slot)
                                return (
                                <div key={index} className="flex flex-col gap-3 p-3 sm:p-4 border border-border rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group relative">
                                    <div className="grid gap-2 w-full">
                                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                                            <CalendarIcon className="w-3.5 h-3.5" /> Date
                                        </Label>
                                        <Popover open={calendarOpen[index] || false} onOpenChange={(open) => setCalendarOpen(prev => ({ ...prev, [index]: open }))}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant={"outline"}
                                                    className={cn(
                                                        "w-full justify-start text-left font-normal bg-card border-border hover:bg-card hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                                        !slot.date && "text-muted-foreground"
                                                    )}
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {slot.date ? format(new Date(slot.date), "PPP") : <span>Pick a date</span>}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 bg-card" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={slot.date ? new Date(slot.date) : undefined}
                                                    onSelect={(date) => {
                                                        updateSlot(index, 'date', date || new Date())
                                                        setCalendarOpen(prev => ({ ...prev, [index]: false }))
                                                    }}
                                                    disabled={{ before: new Date() }}
                                                    className="rounded-md border shadow-md bg-card pointer-events-auto"
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full">
                                        <div className="grid grid-cols-2 gap-3 flex-1">
                                            <div className="grid gap-2">
                                                <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                                                    <Clock className="w-3.5 h-3.5" /> Start
                                                </Label>
                                                <Select value={slot.startTime} onValueChange={(val) => updateSlot(index, 'startTime', val)}>
                                                    <SelectTrigger className={cn(
                                                        "bg-card border-border hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                                        slotInPast && "border-destructive focus:ring-destructive/20 hover:border-destructive"
                                                    )}>
                                                        <SelectValue placeholder="Start" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[200px] bg-card">
                                                        {getTimeOptionsForSlot(slot).map((time) => (
                                                            <SelectItem key={time} value={time}>{time}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="grid gap-2">
                                                <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                                                    <Clock className="w-3.5 h-3.5" /> End
                                                </Label>
                                                <Select value={slot.endTime} onValueChange={(val) => updateSlot(index, 'endTime', val)}>
                                                    <SelectTrigger className={cn(
                                                        "bg-card border-border hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                                        slotInPast && "border-destructive focus:ring-destructive/20 hover:border-destructive"
                                                    )}>
                                                        <SelectValue placeholder="End" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[200px] bg-card">
                                                        {getEndTimeOptionsForSlot(slot).map((time) => (
                                                            <SelectItem key={time} value={time}>{time}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        {timeSlots.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors h-10 w-10 sm:mt-0"
                                                onClick={() => handleRemoveSlot(index)}
                                            >
                                                <Trash2 className="size-4" />
                                            </Button>
                                        )}
                                    </div>
                                    {slotInPast && (
                                        <p className="text-xs text-destructive flex items-center gap-1 -mt-1">
                                            Time slot must be in the future.
                                        </p>
                                    )}
                                </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <Label htmlFor="notes" className="text-sm font-semibold text-foreground">Notes for Candidate</Label>
                        <Textarea
                            id="notes"
                            placeholder="Any specific instructions or topics to prepare..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="min-h-[100px] border-border focus-visible:ring-ring resize-none bg-muted/30"
                        />
                    </div>
                </div>

                <DialogFooter className="bg-muted/50 px-6 py-4 border-t border-border flex gap-3 sm:justify-end">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSubmitting}
                        className="border-border text-foreground hover:bg-muted rounded-xl px-5"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !title || timeSlots.some(s => !s.date || !s.startTime || !s.endTime) || timeSlots.some(isSlotInPast)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl px-6 font-medium"
                    >
                        {isSubmitting ? "Scheduling..." : "Schedule Interview"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
